import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  DEFAULT_MODE, DEFAULT_PRESET, STORAGE_KEYS,
  isThemeMode, isThemePreset, type ThemeMode, type ThemePreset,
} from '@/lib/themes';

/**
 * Theme provider — two orthogonal axes:
 *
 *   mode   light | dark | system   → `.dark` class on <html>
 *   preset one of THEME_PRESETS    → `data-theme-preset` on <body>
 *
 * Every preset ships both a light and a dark palette, so the two never need to
 * know about each other.
 *
 * A small local implementation rather than `next-themes`: that package exists
 * to solve Next.js SSR flash-of-wrong-theme, which a Vite SPA does not have.
 * Adding it would mean shipping a Next dependency into a non-Next app.
 *
 * First paint is handled by the inline script in index.html, which applies the
 * stored values before React mounts. The effects here re-apply the same values,
 * which is a no-op on load and the actual switch thereafter.
 */

interface ThemeContextValue {
  mode: ThemeMode;
  /** What is actually being displayed once `system` is resolved. */
  resolvedMode: 'light' | 'dark';
  setMode: (mode: ThemeMode) => void;
  preset: ThemePreset;
  setPreset: (preset: ThemePreset) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function systemPrefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function readStored<T>(key: string, guard: (v: unknown) => v is T, fallback: T): T {
  try {
    const stored = localStorage.getItem(key);
    if (guard(stored)) return stored;
  } catch {
    // localStorage throws in some private modes; fall through to the default.
  }
  return fallback;
}

function persist(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Preference simply will not survive a reload; not worth failing over.
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() =>
    readStored(STORAGE_KEYS.mode, isThemeMode, DEFAULT_MODE),
  );
  const [preset, setPresetState] = useState<ThemePreset>(() =>
    readStored(STORAGE_KEYS.preset, isThemePreset, DEFAULT_PRESET),
  );
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  // Track OS changes so `system` stays live rather than sampled once at boot.
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const resolvedMode: 'light' | 'dark' = mode === 'system' ? (systemDark ? 'dark' : 'light') : mode;

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', resolvedMode === 'dark');
    // Makes native controls (scrollbars, form widgets) match the theme.
    root.style.colorScheme = resolvedMode;
  }, [resolvedMode]);

  useEffect(() => {
    // On <body>, not <html> — see the `dark` custom-variant note in styles.css.
    // `default` means "no preset": the :root tokens apply unmodified.
    if (preset === 'default') document.body.removeAttribute('data-theme-preset');
    else document.body.setAttribute('data-theme-preset', preset);
  }, [preset]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      mode,
      resolvedMode,
      preset,
      setMode: (next) => {
        setModeState(next);
        persist(STORAGE_KEYS.mode, next);
      },
      setPreset: (next) => {
        setPresetState(next);
        persist(STORAGE_KEYS.preset, next);
      },
    }),
    [mode, resolvedMode, preset],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within <ThemeProvider>');
  return ctx;
}
